import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { canUseOpcBinAnalysis } from '../_helpers/featureAccess';
import {
  downloadOpcBinCsv,
  getOpcBinDistribution,
  getOpcBinHistogram,
  OpcBinDistributionResponse,
  OpcBinHistogramResponse
} from '../services/opc-bin.service';
import { getAllDevicesV2 } from '../services/dashboard-service';
import { formatBytes } from '../services/streaming-download';
import { DeviceAttributes } from '../constants/telosair-interfaces';
import BinContourPlot from '../components/ParticleBins/BinContourPlot';
import BinBoxPlot from '../components/ParticleBins/BinBoxPlot';
import BinBoundariesControl, {
  BoundariesState,
  loadStoredBoundaries
} from '../components/ParticleBins/BinBoundariesControl';
import {
  buildBinDataset,
  buildBinPrompt,
  downloadSizeDistribution
} from '../components/ParticleBins/particleBinExport';
import { stashChartAiHandoff } from '../services/chart-ai-handoff';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';
import Loader from '../components/loader';

const DEFAULT_SERIAL = 4106;

type OpcBinsQuery = {
  serial: string;
  preset: string;
  customStart: string;
  customEnd: string;
  interval: string;
};

const PRESETS: { key: string; label: string; ms: number }[] = [
  { key: '1h', label: '1h', ms: 3600_000 },
  { key: '6h', label: '6h', ms: 6 * 3600_000 },
  { key: '24h', label: '24h', ms: 86400_000 },
  { key: '7d', label: '7d', ms: 7 * 86400_000 },
  { key: '30d', label: '30d', ms: 30 * 86400_000 }
];

const INTERVALS = ['auto', '30s', '1m', '5m', '15m', '1h', '6h', '1d'];

/**
 * A fixed interval as coarse as the window itself (1h over a 1h range) leaves
 * one or two buckets and nothing to contour, so it falls back to the server's
 * auto pick. The threshold stays low deliberately: a coarse-but-plottable
 * combination is the user's choice to make, not ours to override.
 */
const MIN_BUCKETS = 3;

const UNIT_MS: Record<string, number> = { s: 1000, m: 60_000, h: 3600_000, d: 86400_000 };

/**
 * Parse an interval like "15m" into milliseconds.
 * @param interval - Interval string.
 * @returns Milliseconds, or null when the string is not a fixed interval.
 */
function intervalToMs(interval: string): number | null {
  const m = /^(\d+)([smhd])$/.exec(interval);
  if (!m) return null;
  return parseInt(m[1], 10) * UNIT_MS[m[2]];
}

/**
 * Format a millis timestamp as a datetime-local input value.
 * @param ms - Epoch millis.
 * @returns "YYYY-MM-DDTHH:mm" in local time.
 */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  /**
   * Zero-pad to two digits.
   * @param n - Value.
   * @returns Padded string.
   */
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * OPC-N3 particle-size bin analysis page: bar chart, time × bin heat map,
 * contour plot, and per-size box plots for one device's opc_bin0–23 data.
 * @returns The OPC bins page.
 */
export default function OpcBinsPage(): ReactElement {
  const isAllowed = canUseOpcBinAnalysis();
  const navigate = useNavigate();

  // The last query is restored so a refresh or page change keeps the device and range.
  const [storedQuery] = useState<Partial<OpcBinsQuery>>(
    () => getAppliedFilter(Filters.opcBinsQuery)?.data ?? {}
  );
  const [devices, setDevices] = useState<DeviceAttributes[]>([]);
  const [serialInput, setSerialInput] = useState(storedQuery.serial ?? String(DEFAULT_SERIAL));
  const [preset, setPreset] = useState(storedQuery.preset ?? '7d');
  const [customStart, setCustomStart] = useState(
    storedQuery.customStart ?? toLocalInput(Date.now() - 86400_000)
  );
  const [customEnd, setCustomEnd] = useState(storedQuery.customEnd ?? toLocalInput(Date.now()));
  const [interval, setInterval] = useState(storedQuery.interval ?? '1h');

  useEffect(() => {
    setAppliedFilter({
      key: Filters.opcBinsQuery,
      data: { serial: serialInput, preset, customStart, customEnd, interval }
    });
  }, [serialInput, preset, customStart, customEnd, interval]);

  const [histogram, setHistogram] = useState<OpcBinHistogramResponse | null>(null);
  const [distribution, setDistribution] = useState<OpcBinDistributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Bytes received so far; these exports run for minutes, so without visible
  // progress a slow download is indistinguishable from a hung one.
  const [exportBytes, setExportBytes] = useState(0);
  const [boundaries, setBoundaries] = useState<BoundariesState>(loadStoredBoundaries);
  // Time bucket clicked on the contour plot; shared so the box plot can
  // overlay the same moment's size distribution.
  const [sliceTs, setSliceTs] = useState<number[]>([]);

  useEffect(() => {
    getAllDevicesV2()
      .then((res) => {
        if (Array.isArray(res.data)) setDevices(res.data);
      })
      .catch(() => {
        // Device list is a convenience only; manual serial entry still works.
      });
  }, []);

  /**
   * Resolve the effective [start, end] window from preset/custom controls.
   * @returns Window in epoch millis, or null when the custom range is invalid.
   */
  const resolveRange = useCallback((): { start: number; end: number } | null => {
    if (preset !== 'custom') {
      const ms = PRESETS.find((p) => p.key === preset)?.ms ?? 7 * 86400_000;
      const end = Date.now();
      return { start: end - ms, end };
    }
    const start = new Date(customStart).getTime();
    const end = new Date(customEnd).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      toast.error('Custom range is invalid: end must be after start.');
      return null;
    }
    return { start, end };
  }, [preset, customStart, customEnd]);

  const fetchData = useCallback(async (): Promise<void> => {
    const serial = parseInt(serialInput.trim(), 10);
    if (!Number.isFinite(serial) || serial <= 0) {
      toast.error('Enter a valid device serial number.');
      return;
    }
    const range = resolveRange();
    if (!range) return;

    // A fixed interval coarser than the window itself yields a couple of
    // buckets; let the server auto-pick rather than draw a useless plot.
    const fixedMs = interval === 'auto' ? null : intervalToMs(interval);
    const tooCoarse = fixedMs !== null && (range.end - range.start) / fixedMs < MIN_BUCKETS;
    const effectiveInterval = interval === 'auto' || tooCoarse ? undefined : interval;

    setLoading(true);
    try {
      const [histRes, distRes] = await Promise.all([
        getOpcBinHistogram(serial, range.start, range.end, effectiveInterval),
        getOpcBinDistribution(serial, range.start, range.end)
      ]);
      setHistogram(histRes.data);
      setDistribution(distRes.data);
      // Bucket indexes are renumbered by every fetch; a kept slice would
      // silently point at a different moment.
      setSliceTs([]);
      if (histRes.data.timestamps.length === 0) {
        toast.info(`No OPC bin data for device ${serial} in the selected range.`);
      }
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to load OPC bin data.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [serialInput, interval, resolveRange]);

  // Initial load with the restored query, or the defaults (device 4106, last 7 days).
  useEffect(() => {
    if (isAllowed) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export/agent handoff context: the server echoes the window it actually
  // used, which is what the retention clamp may have narrowed it to.
  const exportMeta = useMemo(
    () =>
      histogram
        ? {
            serial: histogram.serial,
            startDate: histogram.startDate,
            endDate: histogram.endDate,
            interval: histogram.interval,
            boundaries: boundaries.edges
          }
        : null,
    [histogram, boundaries.edges]
  );

  const selectedSlices = useMemo(
    () =>
      histogram
        ? sliceTs
            .filter((t) => t >= 0 && t < histogram.timestamps.length)
            .sort((a, b) => a - b)
            .map((t) => ({ timestamp: histogram.timestamps[t], values: histogram.values[t] }))
        : [],
    [sliceTs, histogram]
  );

  /**
   * Download every raw sample in the window. The rows are streamed by the
   * server and never bucketed, so this is the full-resolution data rather than
   * the averaged matrix the charts are drawn from.
   */
  const exportRaw = useCallback(async (): Promise<void> => {
    if (!exportMeta) return;
    setExporting(true);
    setExportBytes(0);
    try {
      await downloadOpcBinCsv(
        exportMeta.serial,
        exportMeta.startDate,
        exportMeta.endDate,
        setExportBytes
      );
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to export OPC bin data.');
    } finally {
      setExporting(false);
    }
  }, [exportMeta]);

  /** Hand the plotted matrix to the agent page with a pre-filled question. */
  const openInAgent = useCallback((): void => {
    if (!exportMeta || !histogram) return;
    stashChartAiHandoff({
      dataset: buildBinDataset(exportMeta, histogram.timestamps, histogram.values),
      prompt: buildBinPrompt(exportMeta)
    });
    navigate('/ai-agent');
  }, [exportMeta, histogram, navigate]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative flex flex-1 flex-col gap-4 px-6 py-6 text-gray-100">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold">OPC-N3 Bin Analysis</h1>
          <p className="text-base text-gray-400">
            Particle-size histogram bins (opc_bin0–23) · device {histogram?.serial ?? serialInput}
          </p>
        </div>
        <div className="flex gap-3 text-base">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Samples</div>
            <div className="text-lg font-semibold">{distribution?.sampleCount?.toLocaleString() ?? '—'}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Buckets</div>
            <div className="text-lg font-semibold">{histogram?.timestamps.length ?? '—'}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Interval</div>
            <div className="text-lg font-semibold">{histogram?.interval ?? '—'}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-800 bg-container-500 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-base text-gray-400" htmlFor="opc-serial">
            Device
            <input
              id="opc-serial"
              list="opc-devices"
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchData();
              }}
              className="w-32 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-100 focus:border-primary-500 focus:outline-none"
            />
            <datalist id="opc-devices">
              {devices.map((d) => (
                <option key={d.id} value={d.serialNumber}>
                  {d.deviceName}
                </option>
              ))}
            </datalist>
          </label>
          <div className="flex items-center gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.key)}
                className={`rounded border px-2.5 py-1.5 text-sm transition ${
                  preset === p.key
                    ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                    : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPreset('custom')}
              className={`rounded border px-2.5 py-1.5 text-sm transition ${
                preset === 'custom'
                  ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                  : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              Custom
            </button>
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2 text-base text-gray-400">
              <input
                type="datetime-local"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
              />
              →
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-base text-gray-400" htmlFor="opc-interval">
            Interval
            <select
              id="opc-interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
            >
              {INTERVALS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 rounded border border-primary-500/40 bg-primary-500/15 px-3 py-1.5 text-base text-primary-400 transition hover:bg-primary-500/25 disabled:opacity-50"
          >
            <span className="material-icons-outlined text-lg">refresh</span>
            Apply
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportRaw}
              disabled={!exportMeta || exporting}
              title="Every raw sample in the window, full resolution (CSV)"
              className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-300 transition hover:text-gray-100 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">download</span>
              {exporting
                ? `Exporting… ${exportBytes > 0 ? formatBytes(exportBytes) : ''}`
                : 'All data'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (exportMeta && distribution) {
                  downloadSizeDistribution(
                    exportMeta,
                    distribution.bins,
                    selectedSlices
                  );
                }
              }}
              disabled={!distribution}
              title={
                selectedSlices.length > 0
                  ? `Window average plus the ${selectedSlices.length} time(s) selected on the contour plot`
                  : 'Window average — click times on the contour plot to add them'
              }
              className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-300 transition hover:text-gray-100 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">download</span>
              Size distribution
            </button>
            <button
              type="button"
              onClick={openInAgent}
              disabled={!histogram || histogram.timestamps.length === 0}
              className="flex items-center gap-1.5 rounded border border-primary-500/40 bg-primary-500/15 px-3 py-1.5 text-base text-primary-400 transition hover:bg-primary-500/25 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">auto_awesome</span>
              AI Insights
            </button>
          </div>
        </div>
        <BinBoundariesControl boundaries={boundaries} onChange={setBoundaries} />
      </div>

      {loading && <Loader />}

      {histogram && (
        <BinContourPlot
          timestamps={histogram.timestamps}
          values={histogram.values}
          boundaries={boundaries.edges}
          sliceTs={sliceTs}
          onSlicesChange={setSliceTs}
        />
      )}
      {distribution && (
        <BinBoxPlot stats={distribution.bins} boundaries={boundaries.edges} overlays={selectedSlices} />
      )}

      {!loading && !histogram && (
        <p className="py-16 text-center text-base text-gray-500">
          Pick a device and time range, then Apply.
        </p>
      )}
    </div>
  );
}
